import { useEffect, useCallback, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  BpError,
  Button,
  ControllerModal,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
} from '@bymason-private/legacy-pattern-components';
import { useCreateGroup, GroupModel } from '@bymason/controller-api-hooks';
import { normalizeToKebabCase } from '@bymason/utils';

export { GroupModel as NewGroup } from '@bymason/controller-api-hooks';

const MAX_GROUP_NAME_LENGTH = 50;
const MAX_GROUP_DESCRIPTION_LENGTH = 300;

interface CreateGroupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newGroup: GroupModel) => void;
  onError?: (message: string, code?: number) => void;
}

export function CreateGroupModal({ onSuccess, onError, onClose, open }: CreateGroupModalProps) {
  const { authToken = '' } = useAuth();
  const [
    createGroup,
    newGroup,
    createGroupError,
    createGroupBusy,
    createGroupCode,
    ,
    clearGroupData,
  ] = useCreateGroup(authToken);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const resetFormState = useCallback(() => {
    setName('');
    setNameError('');
    setDescription('');
    setDescriptionError('');
    clearGroupData();
  }, [clearGroupData]);

  const handleClose = () => {
    onClose();
  };

  const handleCreate = () => {
    createGroup({ name: name.trim(), description });
  };

  const handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const normName = normalizeToKebabCase(evt.target.value || '');
    setName(normName);
    setNameError(validateName(normName));
  };

  const handleDescriptionChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setDescription(value);
    setDescriptionError(validateDescription(value));
  };

  useEffect(() => {
    if (!createGroupBusy && newGroup && onSuccess) {
      onSuccess(newGroup);
    }
  }, [createGroupBusy, newGroup, onSuccess]);

  useEffect(() => {
    let errorMessage = createGroupError;
    if (createGroupCode === 409) {
      errorMessage = `Unable to create group. Group name already exists`;
    }

    if (errorMessage && onError) {
      onError(errorMessage, createGroupCode);
    }
  }, [createGroupCode, createGroupError, newGroup, onError]);

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open, resetFormState]);

  const validateName = (name: string) => {
    if (name.trim().length > MAX_GROUP_NAME_LENGTH) {
      return `Group name length must be ${MAX_GROUP_NAME_LENGTH} characters or less`;
    }
    if (/[^a-zA-Z0-9-_]+/.test(name)) {
      return 'Must only contain [a-z], [A-Z], [0-9], underscore, and dashes';
    }
    return '';
  };
  const validateDescription = (name: string) => {
    if (name.trim().length > MAX_GROUP_DESCRIPTION_LENGTH)
      return `Group description length must be ${MAX_GROUP_DESCRIPTION_LENGTH} characters or less`;
    return '';
  };

  const enableCreate = name && !nameError && description && !descriptionError;

  return (
    <ControllerModal onClose={onClose} open={open} title='Create group' data-modal-name='create-group'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <TextField
            label='Group name'
            helperText={nameError || 'Must only contain [a-z], [A-Z], [0-9], underscore, and dashes'}
            error={!!nameError}
            value={name}
            autoFocus
            onChange={handleNameChange}
            required
            fullWidth
            InputProps={{
              endAdornment: nameError && <BpError color='error' />,
            }}
          />
          <TextField
            label='Group description'
            helperText={descriptionError || 'limit 300 characters'}
            error={!!descriptionError}
            value={description}
            onChange={handleDescriptionChange}
            required
            fullWidth
            InputProps={{
              endAdornment: descriptionError && <BpError color='error' />,
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={handleClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleCreate}
          size='medium'
          variant='contained'
          disabled={!enableCreate}
        >
          Create group
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
